import React, { useEffect, useState, useCallback } from 'react';
import { 
  Heading, SimpleGrid, VStack, Text, HStack, Icon, Box, 
  Card, CardBody, Stat, StatLabel, StatNumber, StatHelpText,
  Grid, GridItem, Progress, List, ListItem, ListIcon, Container
} from '@chakra-ui/react';
import { 
  FiTrendingUp, FiAlertTriangle, FiCheckCircle, FiTarget, 
  FiBarChart2, FiUsers, FiShield, FiActivity, FiClock,
  FiDollarSign, FiCode, FiSettings
} from 'react-icons/fi';
import Page from '../../components/ui/Page';
import SectionCard from '../../components/ui/SectionCard';
import LoadingState from '../../components/ui/LoadingState';
import ErrorState from '../../components/ui/ErrorState';
import useAnalyticsAPI from '../../hooks/useAnalyticsAPI';

export default function AnalysisInsightsPage() {
  const { makeRequest, loading, error } = useAnalyticsAPI();
  const [data, setData] = useState(null);

  const fetchInsights = useCallback(async () => {
    try {
      const result = await makeRequest('/api/bugs/analytics/insights', {});
      
      if (result.status === 'success' && result.data) {
        setData(result.data);
      } else {
        throw new Error('Failed to load insights data');
      }
    } catch (err) {
      console.error('Failed to fetch insights:', err);
    }
  }, [makeRequest]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const insights = data?.aiEnhancedInsights;

  if (loading) return <LoadingState title="Loading AI Insights" minH="70vh" />;
  if (error) return <ErrorState message={error} />;
  if (!insights) return <ErrorState message="No insights data available" />;

  const getRiskColor = (riskLevel) => {
    switch (riskLevel?.toUpperCase()) {
      case 'HIGH': return 'red';
      case 'MEDIUM': return 'orange';
      case 'LOW': return 'green';
      default: return 'gray';
    }
  };

  const getRiskIcon = (riskLevel) => {
    switch (riskLevel?.toUpperCase()) {
      case 'HIGH': return FiAlertTriangle;
      case 'MEDIUM': return FiTarget;
      case 'LOW': return FiCheckCircle;
      default: return FiActivity;
    }
  };

  return (
    <Page maxW="full">
      <Container maxW="8xl" p={0}>
        {/* Header */}
        <Box mb={8}>
          <Heading size="xl" color="gray.600" mb={2}>AI-Enhanced Insights Dashboard</Heading>
          <Text fontSize="lg" color="gray.500">
            Comprehensive analysis powered by artificial intelligence
          </Text>
        </Box>

      {/* Risk Assessment Overview */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Risk Level</StatLabel>
              <StatNumber color={`${getRiskColor(insights.riskLevel)}.500`}>
                <HStack>
                  <Icon as={getRiskIcon(insights.riskLevel)} />
                  <Text>{insights.riskLevel || 'UNKNOWN'}</Text>
                </HStack>
              </StatNumber>
              <StatHelpText>
                <Icon as={FiShield} mr={1} />
                Overall risk assessment
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Risk Score</StatLabel>
              <StatNumber color="blue.500">{insights.riskScore || 0}/100</StatNumber>
              <StatHelpText>
                <Progress value={insights.riskScore || 0} size="sm" colorScheme="blue" />
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Key Actions</StatLabel>
              <StatNumber color="green.500">
                {insights.keyRecommendations?.length || 0}
              </StatNumber>
              <StatHelpText>
                <Icon as={FiTarget} mr={1} />
                Recommended actions
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>Analysis Status</StatLabel>
              <StatNumber color="purple.500">
                <HStack>
                  <Icon as={FiCheckCircle} />
                  <Text>Complete</Text>
                </HStack>
              </StatNumber>
              <StatHelpText>
                <Icon as={FiActivity} mr={1} />
                AI analysis finished
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6}>
        {/* Main Content */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Executive Summary */}
            {insights.executiveSummary && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiBarChart2} color="blue.500" />
                    <Heading size="md">Executive Summary</Heading>
                  </HStack>
                  <Text color="gray.700" lineHeight="1.6">
                    {insights.executiveSummary}
                  </Text>
                </VStack>
              </SectionCard>
            )}

            {/* Trend Analysis */}
            {insights.trendAnalysis && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiTrendingUp} color="green.500" />
                    <Heading size="md">Trend Analysis</Heading>
                  </HStack>
                  <Text color="gray.700" lineHeight="1.6">
                    {insights.trendAnalysis}
                  </Text>
                </VStack>
              </SectionCard>
            )}

            {/* Root Cause Trends */}
            {insights.rootCauseTrends && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiTarget} color="orange.500" />
                    <Heading size="md">Root Cause Analysis</Heading>
                  </HStack>
                  {typeof insights.rootCauseTrends === 'string' ? (
                    <Text color="gray.700" lineHeight="1.6">
                      {insights.rootCauseTrends}
                    </Text>
                  ) : (
                    <VStack align="start" spacing={3}>
                      {Object.entries(insights.rootCauseTrends).map(([key, value]) => (
                        <Box key={key} p={3} bg="gray.50" borderRadius="md" w="full">
                          <Text fontWeight="bold" color="gray.800" mb={1}>
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </Text>
                          <Text color="gray.600" fontSize="sm">
                            {typeof value === 'string' ? value : JSON.stringify(value)}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </SectionCard>
            )}

            {/* Business Impact Analysis */}
            {insights.businessImpactAnalysis && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiDollarSign} color="green.500" />
                    <Heading size="md">Business Impact Analysis</Heading>
                  </HStack>
                  {typeof insights.businessImpactAnalysis === 'string' ? (
                    <Text color="gray.700" lineHeight="1.6">
                      {insights.businessImpactAnalysis}
                    </Text>
                  ) : (
                    <VStack align="start" spacing={3}>
                      {Object.entries(insights.businessImpactAnalysis).map(([key, value]) => (
                        <Box key={key} p={3} bg="green.50" borderRadius="md" w="full">
                          <Text fontWeight="bold" color="green.800" mb={1}>
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </Text>
                          <Text color="green.700" fontSize="sm">
                            {typeof value === 'string' ? value : JSON.stringify(value)}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </SectionCard>
            )}
          </VStack>
        </GridItem>

        {/* Sidebar */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Key Recommendations */}
            {insights.keyRecommendations && insights.keyRecommendations.length > 0 && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiTarget} color="blue.500" />
                    <Heading size="md">Key Recommendations</Heading>
                  </HStack>
                  <List spacing={3}>
                    {insights.keyRecommendations.map((recommendation, index) => (
                      <ListItem key={index}>
                        <ListIcon as={FiCheckCircle} color="green.500" />
                        <Text fontSize="sm" color="gray.700">
                          {typeof recommendation === 'string' ? recommendation : recommendation.title || recommendation.description}
                        </Text>
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              </SectionCard>
            )}

            {/* Technical Debt Indicators */}
            {insights.technicalDebtIndicators && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiCode} color="orange.500" />
                    <Heading size="md">Technical Debt</Heading>
                  </HStack>
                  {typeof insights.technicalDebtIndicators === 'string' ? (
                    <Text color="gray.700" fontSize="sm" lineHeight="1.6">
                      {insights.technicalDebtIndicators}
                    </Text>
                  ) : (
                    <VStack align="start" spacing={2}>
                      {Object.entries(insights.technicalDebtIndicators).map(([key, value]) => (
                        <Box key={key} p={2} bg="orange.50" borderRadius="md" w="full">
                          <Text fontWeight="bold" color="orange.800" fontSize="sm">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </Text>
                          <Text color="orange.700" fontSize="xs">
                            {typeof value === 'string' ? value : JSON.stringify(value)}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </SectionCard>
            )}

            {/* Organizational Health */}
            {insights.organizationalHealthIndicators && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiUsers} color="purple.500" />
                    <Heading size="md">Organizational Health</Heading>
                  </HStack>
                  {typeof insights.organizationalHealthIndicators === 'string' ? (
                    <Text color="gray.700" fontSize="sm" lineHeight="1.6">
                      {insights.organizationalHealthIndicators}
                    </Text>
                  ) : (
                    <VStack align="start" spacing={2}>
                      {Object.entries(insights.organizationalHealthIndicators).map(([key, value]) => (
                        <Box key={key} p={2} bg="purple.50" borderRadius="md" w="full">
                          <Text fontWeight="bold" color="purple.800" fontSize="sm">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </Text>
                          <Text color="purple.700" fontSize="xs">
                            {typeof value === 'string' ? value : JSON.stringify(value)}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </SectionCard>
            )}

            {/* Quarterly Outlook */}
            {insights.quarterlyOutlook && (
              <SectionCard>
                <VStack align="start" spacing={4}>
                  <HStack>
                    <Icon as={FiClock} color="teal.500" />
                    <Heading size="md">Quarterly Outlook</Heading>
                  </HStack>
                  {typeof insights.quarterlyOutlook === 'string' ? (
                    <Text color="gray.700" fontSize="sm" lineHeight="1.6">
                      {insights.quarterlyOutlook}
                    </Text>
                  ) : (
                    <VStack align="start" spacing={2}>
                      {Object.entries(insights.quarterlyOutlook).map(([key, value]) => (
                        <Box key={key} p={2} bg="teal.50" borderRadius="md" w="full">
                          <Text fontWeight="bold" color="teal.800" fontSize="sm">
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </Text>
                          <Text color="teal.700" fontSize="xs">
                            {typeof value === 'string' ? value : JSON.stringify(value)}
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </VStack>
              </SectionCard>
            )}
          </VStack>
        </GridItem>
      </Grid>

      {/* Recommended Actions */}
      {insights.recommendedActions && (
        <SectionCard mt={6}>
          <VStack align="start" spacing={4}>
            <HStack>
              <Icon as={FiSettings} color="blue.500" />
              <Heading size="md">Recommended Actions</Heading>
            </HStack>
            {typeof insights.recommendedActions === 'string' ? (
              <Text color="gray.700" lineHeight="1.6">
                {insights.recommendedActions}
              </Text>
            ) : (
              <VStack align="start" spacing={3} w="full">
                {Object.entries(insights.recommendedActions).map(([key, value]) => (
                  <Box key={key} p={4} bg="blue.50" borderRadius="md" w="full" borderLeft="4px solid" borderColor="blue.500">
                    <Text fontWeight="bold" color="blue.800" mb={2}>
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </Text>
                    <Text color="blue.700" fontSize="sm">
                      {typeof value === 'string' ? value : JSON.stringify(value)}
                    </Text>
                  </Box>
                ))}
              </VStack>
            )}
          </VStack>
        </SectionCard>
      )}
      </Container>
    </Page>
  );
}


